import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import 'core-js/stable';
import 'regenerator-runtime/runtime';
import './input.css';
import './styles/theme.css';
import './i18n/i18n';

import App from './App';
import FullPageLoader from './components/FullPageLoader';
import { initializeApp } from './config/init';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { captureError, initSentryWeb } from './observability/sentry';
import GoogleAuthProvider from './providers/GoogleAuthProvider';

const LoadingScreen: React.FC = () => <FullPageLoader message="Carregando aplicacao..." />;

const ErrorScreen: React.FC<{ message?: string }> = ({ message }) => (
  <div className="flex min-h-screen items-center justify-center bg-bg px-4 text-text">
    <div className="w-full max-w-xl rounded-2xl border border-danger/30 bg-surface px-6 py-8 text-center shadow-calm">
      <h1 className="mb-3 text-2xl font-bold text-danger">Erro ao carregar a aplicacao</h1>
      <p className="text-base text-muted">{message || 'Por favor, tente novamente mais tarde.'}</p>
    </div>
  </div>
);
const RuntimeErrorFallback: React.FC = () => (
  <ErrorScreen message="Ocorreu um erro inesperado. Tente novamente." />
);

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Elemento 'root' nao encontrado no DOM.");
}

initSentryWeb();

const root = ReactDOM.createRoot(rootElement);

async function runApp() {
  root.render(<LoadingScreen />);

  try {
    await initializeApp();

    root.render(
      <React.StrictMode>
        <Sentry.ErrorBoundary fallback={<RuntimeErrorFallback />}>
          <ThemeProvider>
            <BrowserRouter>
              <AuthProvider>
                <GoogleAuthProvider>
                  <App />
                </GoogleAuthProvider>
              </AuthProvider>
            </BrowserRouter>
          </ThemeProvider>
        </Sentry.ErrorBoundary>
      </React.StrictMode>,
    );
  } catch (error: unknown) {
    captureError(error, { stage: 'runApp.initialize' }, { feature: 'bootstrap' });
    const message = error instanceof Error ? error.message : undefined;
    root.render(<ErrorScreen message={message} />);
  }
}

void runApp();
